import Phaser from "phaser";
import { BaseLevelScene, objProp } from "./BaseLevelScene";
import { levelById } from "../levels";
import { Prop } from "../tiles";
import { Tex } from "../ui/textures";
import { Palette } from "../ui/palette";
import { ENEMY, registerEnemyAnims } from "../ui/enemies";
import { AA, registerAndrewAnims, useAndrewSprite, andrewAnimMs, andrewThrowBall } from "../ui/andrewAnim";
import { ENEMY_BASE } from "../state/scoring";

// Auto-run ramps from a slower start to a faster finish across the level (by how far right
// Andrew has travelled), so the track builds pace as it progresses.
const RUN_SPEED_START = 225;
const RUN_SPEED_END = 510;
const JUMP_SPEED = 440;

// Power-up run freeze: the transformation burst has Andrew running at its head and
// tail, so he keeps moving for the first LEAD_IN frames and the last LEAD_OUT
// frames, and stops (velocity 0) through the middle while it plays. Tune freely;
// the burst is POWERUP.frames long.
const POWERUP_LEAD_IN = 0;
const POWERUP_LEAD_OUT = 6;

// On-screen size of the throwable tokens (code / Claude pickup + thrown projectile),
// normalized from their source art. One knob keeps the pickup and the flying token
// the same size.
const THROWABLE_PX = 23;

// Enemy on-screen heights. The beast art is huge at native size; these are the
// single knobs that scale each bug (art + Arcade body together). Tune freely.
const BUG_H = 40; // 2x, sized up for the larger Andrew / bigger world
const BEAST_H = 72;
// The crawl frames have transparent padding around the art, so the full-frame
// Arcade body overreaches the visible bug. Trim the body to this fraction of the
// frame (centered), so the hit area hugs the bug instead of the padding.
const BUG_HIT_W = 0.55;
const BUG_HIT_H = 0.62;

// Stomp: land on a bug's top (falling, feet within STOMP_MARGIN px of its top) to
// squash it and bounce back up. Any other contact is still a hit.
const STOMP_MARGIN = 16;
const STOMP_BOUNCE = 340;
// The authored "jumped on" deaths (squash to a dust pile), packed by
// tools/pack-enemy.mjs. Each plays once and holds the final dust frame in place. If
// the art isn't loaded, a squash tween stands in instead.
const BUG_DIE_KEY = "bug-die";
const BUG_DIE_ANIM = "bug-die";
const BEAST_DIE_KEY = "beast-die";
const BEAST_DIE_ANIM = "beast-die";
// Height (px) of the subject in the FIRST death frame within its packed sheet (from
// pack-enemy's output). The death sheet is normalized to the wide dust pile, so its
// subject is smaller than the crawl's; scaling by targetHeight/thisHeight makes the
// dying bug start at the same size as the crawling one instead of snapping tiny.
const BUG_DIE_FRAME0_H = 39;
const BEAST_DIE_FRAME0_H = 64;

// Andrew's Claude power-up transformation burst (packed by tools/pack-enemy.mjs).
const POWERUP = { key: "andrew-powerup", frames: 28, fw: 256, fh: 237, anim: "andrew-powerup-fx" };
const POWERUP_H = 170; // on-screen height of the burst, scaled up with ANDREW_SCALE

/** Andrew - auto-run speed level. You own jump + throw; the run is automatic.
 *  Throw code tokens (tier 1) to squash small bugs; upgrade to Claude tokens
 *  (tier 2) to take down the big ones. Collect feature requests. */
export class Level3AndrewScene extends BaseLevelScene {
  private tier = 1; // starts as code tokens
  private projectiles!: Phaser.Physics.Arcade.Group;
  private bugs!: Phaser.Physics.Arcade.Group;
  private started = false; // auto-run begins only after the 3-2-1 countdown
  private runFrozen = false; // held still through the middle of the power-up burst
  private throwUntil = 0; // time.now the throw pose holds until
  private heldBall?: Phaser.GameObjects.Image; // the token in Andrew's hand mid-throw
  private thrownFor = -1; // throwUntil value already launched, so each throw fires once
  private countText?: Phaser.GameObjects.Text;

  constructor() {
    super("Level3Andrew");
  }

  create() {
    this.tier = 1;
    this.started = false;
    this.runFrozen = false;
    this.throwUntil = 0;
    this.thrownFor = -1;
    this.heldBall = undefined;
    this.buildWorld(levelById("andrew"));
    this.world.setVisible(false); // invisible floor: Andrew runs on the painted bionic wall
    this.buildBionicBackground();
    this.projectiles = this.physics.add.group();
    this.bugs = this.physics.add.group();
    registerEnemyAnims(this);
    // Bug / beast deaths (one-shot squash-to-dust) - registered here since Andrew-only.
    for (const [key, anim] of [
      [BUG_DIE_KEY, BUG_DIE_ANIM],
      [BEAST_DIE_KEY, BEAST_DIE_ANIM],
    ] as const) {
      if (this.textures.exists(key) && !this.anims.exists(anim)) {
        this.anims.create({
          key: anim,
          frames: this.anims.generateFrameNumbers(key, {}),
          frameRate: 16,
          repeat: 0, // play once, then hold the final dust frame
        });
      }
    }
    if (!this.anims.exists(POWERUP.anim)) {
      this.anims.create({
        key: POWERUP.anim,
        frames: this.anims.generateFrameNumbers(POWERUP.key, { start: 0, end: POWERUP.frames - 1 }),
        frameRate: 10, // slow burst; also keeps Andrew floating longer through the frozen middle
        repeat: 0,
      });
    }

    const spawn = this.findSpawn();
    this.createPlayer(spawn.x, spawn.y);
    // Swap the placeholder rect for the real animated art (idle/run/jump/throw).
    registerAndrewAnims(this);
    useAndrewSprite(this.player);
    this.player.setDepth(10);
    // Position the held token after physics has moved Andrew this frame (so it sits
    // in his hand, not a frame behind), and launch it at the throw's release frame.
    this.events.on(Phaser.Scenes.Events.POST_UPDATE, this.updateThrowBall, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      this.events.off(Phaser.Scenes.Events.POST_UPDATE, this.updateThrowBall, this),
    );

    this.forEachObject((obj, cls) => {
      switch (cls) {
        case "collectible":
          this.spawnCollectible(obj);
          break;
        case "sheepy":
          this.spawnSheepy(obj);
          break;
        case "throwable_pickup":
          this.makePickup(obj, objProp(obj, Prop.TIER, 2));
          break;
        case "bug":
          this.makeBug(obj, objProp(obj, Prop.SIZE, "small"));
          break;
        case "exit":
          this.makeExit(obj);
          break;
      }
    });

    this.physics.add.collider(this.bugs, this.world);
    this.physics.add.collider(this.projectiles, this.world, (p) =>
      this.resolveThrow(p as Phaser.GameObjects.GameObject),
    );
    this.physics.add.overlap(this.projectiles, this.bugs, (p, b) =>
      this.hitBug(p as Phaser.Physics.Arcade.Sprite, b as Phaser.Physics.Arcade.Sprite),
    );
    this.physics.add.overlap(this.player, this.bugs, (_p, b) =>
      this.onBugTouch(b as Phaser.Physics.Arcade.Sprite),
    );

    this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
    this.cameras.main.startFollow(this.player, true, 0.15, 0.1);
    this.cameras.main.setFollowOffset(-120, 0); // look ahead down the track

    this.buildHud();
    this.buildInput();
    this.updateInfo();
    this.startCountdown();
  }

  /** Pre-run beat: Andrew stands playing his idle while a 3-2-1 counts down, and
   *  the idle finishes exactly on "Go!" - at which point auto-run begins. The
   *  count timings are pinned to the idle clip's length so the two line up. */
  private startCountdown() {
    this.player.play(AA.idle); // one-shot; completes on "Go!"
    const dur = andrewAnimMs(this, AA.idle) || 3000;
    this.showCount("3");
    this.time.delayedCall(dur / 3, () => this.showCount("2"));
    this.time.delayedCall((dur * 2) / 3, () => this.showCount("1"));
    this.player.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.showCount("Go!");
      this.started = true; // update() begins the auto-run this frame
      this.time.delayedCall(600, () => {
        this.tweens.add({ targets: this.countText, alpha: 0, duration: 250, onComplete: () => this.countText?.destroy() });
      });
    });
  }

  /** Big centered countdown number, popped in over the play area. */
  private showCount(msg: string) {
    this.countText?.destroy();
    this.countText = this.add
      .text(this.scale.width / 2, this.scale.height / 2 - 40, msg, {
        fontFamily: "monospace",
        fontSize: "72px",
        color: "#ffd447",
        stroke: "#0e0f13",
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1500)
      .setScale(0.6);
    this.tweens.add({ targets: this.countText, scale: 1, duration: 180, ease: "Back.easeOut" });
  }

  private tierName() {
    return this.tier >= 2 ? "Claude token" : "code token";
  }
  private updateInfo() {
    this.setHudInfo(`Ammo: ${this.tierName()}\nStomp or throw · features`);
  }

  private makePickup(obj: Phaser.Types.Tilemaps.TiledObject, tier: number) {
    const isClaude = tier >= 2;
    const pickup = this.physics.add.sprite(obj.x!, obj.y!, isClaude ? Tex.claudeMega : Tex.throwCode);
    if (isClaude) {
      const coinAnim = "claude-coin-spin";
      if (!this.anims.exists(coinAnim)) {
        this.anims.create({ key: coinAnim, frames: this.anims.generateFrameNumbers(Tex.claudeMega, { start: 0, end: 12 }).reverse(), frameRate: 12, repeat: -1 });
      }
      pickup.setScale(64 / 512);
      pickup.play(coinAnim);
    } else {
      pickup.setScale(THROWABLE_PX / Math.max(pickup.width, pickup.height));
    }
    (pickup.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.physics.add.overlap(this.player, pickup, () => {
      if (!pickup.active) return;
      pickup.destroy();
      const was = this.tier;
      this.tier = Math.max(this.tier, tier);
      if (this.tier >= 2 && was < 2) this.playPowerup(); // Claude transformation burst
      this.flashText("Upgraded → Claude tokens", Palette.andrew);
      this.updateInfo();
    });
  }

  /** One-shot Claude power-up burst, centered on Andrew and following him while it
   *  plays. Andrew is hidden under it (the burst contains him) and restored after. */
  private playPowerup() {
    const fx = this.add.sprite(this.player.x, this.player.y, POWERUP.key).setDepth(20);
    fx.setScale(POWERUP_H / fx.height);
    this.player.setVisible(false);
    // Lock him in the air the instant he grabs the token, so he doesn't fall
    // during the burst's first frames before the float window opens.
    this.runFrozen = true;
    (this.player.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.player.setVelocity(0, 0);
    const follow = () => { if (this.player.active) fx.setPosition(this.player.x, this.player.y); };
    this.events.on(Phaser.Scenes.Events.POST_UPDATE, follow);
    // Stop the run through the middle of the burst; keep moving during the running
    // lead-in and lead-out frames (frame.index is 1-based).
    fx.on(Phaser.Animations.Events.ANIMATION_UPDATE, (_a: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
      this.runFrozen = frame.index > POWERUP_LEAD_IN && frame.index <= POWERUP.frames - POWERUP_LEAD_OUT;
    });
    fx.play(POWERUP.anim);
    fx.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.events.off(Phaser.Scenes.Events.POST_UPDATE, follow);
      this.runFrozen = false;
      (this.player.body as Phaser.Physics.Arcade.Body).setAllowGravity(true); // resume falling/running
      fx.destroy();
      if (this.player.active) this.player.setVisible(true);
    });
  }

  private makeBug(obj: Phaser.Types.Tilemaps.TiledObject, size: string) {
    const big = size === "big";
    const e = big ? ENEMY.beast : ENEMY.bug;
    const bug = this.bugs.create(obj.x!, obj.y!, e.key) as Phaser.Physics.Arcade.Sprite;
    bug.play(e.anim);
    bug.setScale((big ? BEAST_H : BUG_H) / bug.height); // scales art + Arcade body together
    // Shrink the body to hug the visible bug (frame has transparent padding). setSize
    // uses unscaled frame px; the sprite scale above then applies to the body too.
    const fw = bug.width;
    const fh = bug.height;
    bug.body!.setSize(fw * BUG_HIT_W, fh * BUG_HIT_H);
    bug.body!.setOffset((fw - fw * BUG_HIT_W) / 2, (fh - fh * BUG_HIT_H) / 2);
    bug.setData("size", size);
    bug.setVelocityX(-30);
    bug.setBounce(1, 0);
    bug.setCollideWorldBounds(false);
  }

  /** Tile the five interchangeable wall segments across the full level width, picking
   *  a random one for each 1536px slot (never the same twice in a row). The wall is
   *  purely decorative and world-locked (no parallax), sitting behind everything. */
  private buildBionicBackground() {
    const BG_SCALE = 0.5; // wall graphics rendered at half size (1536x1024 -> 768x512)
    const SEG_W = 1536 * BG_SCALE; // on-screen width of one segment
    const keys = [1, 2, 3, 4, 5].map((i) => `bionic-seg-${i}`).filter((k) => this.textures.exists(k));
    if (!keys.length) return;
    let prev = "";
    for (let x = 0; x < this.map.widthInPixels; x += SEG_W) {
      let key = Phaser.Utils.Array.GetRandom(keys);
      if (keys.length > 1) while (key === prev) key = Phaser.Utils.Array.GetRandom(keys);
      prev = key;
      this.add.image(x, 0, key).setOrigin(0, 0).setScale(BG_SCALE).setDepth(-10);
    }
  }

  private makeExit(obj: Phaser.Types.Tilemaps.TiledObject) {
    const zone = this.add.zone(obj.x!, obj.y!, obj.width!, obj.height!).setOrigin(0, 0);
    this.physics.add.existing(zone, true);
    this.physics.add.overlap(this.player, zone, () => this.reachExit());
  }

  private hitBug(p: Phaser.Physics.Arcade.Sprite, bug: Phaser.Physics.Arcade.Sprite) {
    if (!bug.active || !p.active) return;
    const need = bug.getData("size") === "big" ? 2 : 1;
    const projTier = (p.getData("tier") as number) ?? 1;
    if (projTier >= need) {
      // Pierce: the token keeps flying after a kill so one throw can chain multiple
      // bugs. The kill is batched onto the projectile and scored in resolveThrow.
      this.killBug(bug, p as Phaser.GameObjects.GameObject); // same squash-to-dust death as a stomp
    } else {
      // code token bounces off a big bug - need the Claude token. This ends the throw.
      bug.setTint(0xffd447);
      this.time.delayedCall(150, () => bug.active && bug.clearTint());
      this.flashText("Big bug needs a Claude token!", Palette.textDim);
      this.resolveThrow(p as Phaser.GameObjects.GameObject);
    }
  }

  /** Andrew touched a bug. Coming down onto its top (falling, feet within
   *  STOMP_MARGIN of the bug top) squashes it and bounces him; anything else is a
   *  hit. This lets both small bugs and the beast be killed by jumping on them. */
  private onBugTouch(bug: Phaser.Physics.Arcade.Sprite) {
    const bb = bug.body as Phaser.Physics.Arcade.Body;
    if (this.finished || !bug.active || !bb.enable) return; // ignore already-dead dust
    const pb = this.player.body as Phaser.Physics.Arcade.Body;
    const stomping = pb.velocity.y > 0 && pb.bottom <= bb.top + STOMP_MARGIN;
    if (!stomping) {
      this.failLevel("A bug got you.");
      return;
    }
    this.killBug(bug);
    this.player.setVelocityY(-STOMP_BOUNCE); // hop off the squashed bug
  }

  /** Kill a bug: score it, stop it dead, and play its squash-to-dust death, left in
   *  place on the ground. Shared by stomps and throwable hits; falls back to a quick
   *  flatten-and-fade if the death art isn't loaded. */
  private killBug(bug: Phaser.Physics.Arcade.Sprite, projectile?: Phaser.GameObjects.GameObject) {
    const big = bug.getData("size") === "big";
    if (!(bug.body as Phaser.Physics.Arcade.Body).enable) return; // already dying
    // Stop it the instant the death starts: no velocity, no more physics/contact.
    bug.setVelocity(0, 0);
    (bug.body as Phaser.Physics.Arcade.Body).enable = false;
    const base = ENEMY_BASE[big ? "big" : "small"];
    if (projectile) {
      // Thrown kill: batch it onto the projectile so the combo/throw multiplier
      // applies when the token is spent (see resolveThrow).
      this.recordThrowHit(projectile, base, true);
    } else {
      // Stomp: manual kill scores its base immediately at x1.
      this.score += base;
      this.scoreParts.enemies += base;
    }
    this.refreshHud();
    const dieAnim = big ? BEAST_DIE_ANIM : BUG_DIE_ANIM;
    if (this.anims.exists(dieAnim)) {
      // The death sheet is normalized to its wide dust pile, so its subject is smaller
      // than the crawl frame. Scale so the first death frame starts at the crawling
      // bug's size, keep the ground line fixed, then hold the final dust frame forever.
      const groundY = bug.getBottomCenter().y;
      const frame0H = big ? BEAST_DIE_FRAME0_H : BUG_DIE_FRAME0_H;
      const targetH = big ? BEAST_H : BUG_H;
      bug.play(dieAnim);
      bug.setOrigin(0.5, 1).setScale(targetH / frame0H).setPosition(bug.x, groundY);
    } else {
      this.tweens.add({
        targets: bug,
        scaleY: bug.scaleY * 0.25,
        scaleX: bug.scaleX * 1.2,
        alpha: 0,
        duration: 180,
        ease: "Quad.easeOut",
        onComplete: () => bug.destroy(),
      });
    }
  }

  /** Launch a thrown token from (x, y) - the hand position at the release frame. */
  private launchToken(x: number, y: number) {
    const claude = this.tier >= 2;
    const p = this.projectiles.create(x, y, claude ? Tex.throwClaude : Tex.throwCode) as Phaser.Physics.Arcade.Sprite;
    p.setData("tier", this.tier);
    p.setScale(THROWABLE_PX / Math.max(p.width, p.height));
    // Fire fast down the track but riding low, aimed slightly downward so it stays
    // near the ground where the small bugs are; gravity (world y:900) drops it the
    // rest of the way.
    p.setVelocity(980, 20); // fly much farther (flatter, faster)
    p.setAngularVelocity(540); // spin as it flies (Andrew always throws right; visual only)
    this.time.delayedCall(1400, () => {
      if (p.active) this.resolveThrow(p as Phaser.GameObjects.GameObject);
    });
  }

  /** POST_UPDATE: hold the token in Andrew's hand through the windup (per the
   *  authored per-frame offsets), then launch it once at the release frame from
   *  the last in-hand spot. Andrew always faces right, so no facing flip. */
  private updateThrowBall() {
    if (this.finished) return;
    const st = this.time.now < this.throwUntil ? andrewThrowBall(this.player) : null;
    if (!st || !this.player.visible) {
      this.heldBall?.setVisible(false);
      return;
    }
    const tex = this.tier >= 2 ? Tex.throwClaude : Tex.throwCode;
    if (!st.released) {
      // Offsets are frame-local px (authored in throw.html), so scale them by the
      // sprite's actual on-screen scale, not the raw ANDREW_SCALE.
      const x = this.player.x + st.ox * this.player.scaleX;
      const y = this.player.y + st.oy * this.player.scaleY;
      if (!this.heldBall) this.heldBall = this.add.image(x, y, tex);
      else this.heldBall.setTexture(tex);
      this.heldBall
        .setScale(THROWABLE_PX / Math.max(this.heldBall.width, this.heldBall.height))
        .setPosition(x, y)
        .setVisible(true)
        .setDepth(this.player.depth + (st.front ? 1 : -1));
      return;
    }
    this.heldBall?.setVisible(false);
    if (this.thrownFor !== this.throwUntil) {
      this.thrownFor = this.throwUntil; // one launch per throw
      this.launchToken(this.player.x + st.launchOx * this.player.scaleX, this.player.y + st.launchOy * this.player.scaleY);
    }
  }

  private reachExit() {
    const pct = this.totalCollectibles ? this.collectedCount / this.totalCollectibles : 0;
    const stars = pct >= 0.7 ? 3 : pct >= 0.4 ? 2 : 1;
    this.completeLevel(stars, [
      "Great building and bug smashing!",
      `Feature requests: ${this.collectedCount}/${this.totalCollectibles}.`,
    ]);
  }

  override update() {
    if (this.finished) return;
    const body = this.player.body as Phaser.Physics.Arcade.Body;

    // Bugs patrol and bounce off walls; face their travel (the art faces left).
    for (const b of this.bugs.getChildren()) {
      const s = b as Phaser.Physics.Arcade.Sprite;
      const vx = (s.body as Phaser.Physics.Arcade.Body).velocity.x;
      if (Math.abs(vx) > 1) s.setFlipX(vx > 0);
    }

    // Before "Go!": stand still and let the idle countdown play out.
    if (!this.started) {
      this.player.setVelocityX(0);
      return;
    }

    if (this.runFrozen) {
      // Middle of the power-up burst: float in place (gravity off) so he hangs in
      // the air through the transformation instead of falling back down.
      body.setAllowGravity(false);
      this.player.setVelocity(0, 0);
    } else {
      // Auto-run right (lead-in / lead-out of the burst, and normal play). Speed ramps up
      // with how far he's travelled, so he starts slower and quickens as the level goes on.
      body.setAllowGravity(true);
      const progress = Phaser.Math.Clamp(this.player.x / this.map.widthInPixels, 0, 1);
      this.player.setVelocityX(Phaser.Math.Linear(RUN_SPEED_START, RUN_SPEED_END, progress));
      if (this.jumpJustDown() && body.blocked.down) this.player.setVelocityY(-JUMP_SPEED);
      if (this.throwJustDown()) {
        // Restart the clip from frame 0 every press (like Dan). Mashing keeps
        // resetting before the release frame, so a token only leaves his hand
        // when the throw actually reaches release - no throwing faster than the
        // animation. The launch itself fires in updateThrowBall.
        this.throwUntil = this.time.now + andrewAnimMs(this, AA.throw);
        this.player.play(AA.throw);
      }
    }

    this.updateAndrewAnim();

    // fell in a pit
    if (this.player.y > this.map.heightInPixels + 60) this.failLevel("Fell into a pit.");
  }

  /** Drive Andrew's animation from physics state. Priority is throw-until-released >
   *  airborne > throw-follow-through > run. The throw clip only *has* to own the
   *  sprite until its token leaves his hand (updateThrowBall reads the live throw
   *  frame to hold + launch the token); once it has launched, being airborne wins so
   *  a jump right after a throw reads as a jump instead of a frozen throw pose. While
   *  the power-up burst hides him under the fx, leave his current anim alone. */
  private updateAndrewAnim() {
    const player = this.player;
    if (!player.visible) return; // hidden under the power-up burst
    const body = player.body as Phaser.Physics.Arcade.Body;
    const cur = player.anims.currentAnim?.key;

    const throwing = this.time.now < this.throwUntil;
    const tokenLaunched = this.thrownFor === this.throwUntil;
    if (throwing && !tokenLaunched) return; // hold the throw clip until the token launches
    if (!body.blocked.down) {
      if (cur !== AA.jump) player.play(AA.jump); // one-shot, holds the falling frame
      return;
    }
    if (throwing) return; // grounded: let the throw clip play its follow-through
    if (cur !== AA.run || !player.anims.isPlaying) player.play(AA.run, true);
  }
}
